import { useUserStore, useAppStore, useSessionStore, useNotificationStore, useTransformStore } from '@/stores'
import { useAppMachine } from '@/state'
import { jsonRpc } from '@communication'
import { startLiveCaption, stopLiveCaption } from '@/messages/liveCaptionMessage'
import { localStream } from '@stream'
import { TFunction } from 'i18next'
import { EventBus } from 'communication/src/EventBus'

interface AudioProcessor {
  context: AudioContext
  worklet: AudioWorkletNode
  source: MediaStreamAudioSourceNode
}

interface TrancsriptEvent {
  party: string
  transcript: string
  isPartial: boolean
  resultId: string
  partyId: string
  languageCode: string
}
const AUDIO_SEND_CHUNK_BYTES = 6400

interface EventList {
  cueChanged: TextTrack
}
export default class LiveCaptionProcedure extends EventBus<EventList> {
  private name: string = 'liveCaptionProcedure'
  private audioProcessor?: AudioProcessor
  // Bumped on every close so a processor still being created knows it was stopped or superseded
  private audioProcessorVersion: number = 0
  // True between stt:start and stt:stop, i.e. while the microphone should be streamed
  private isLiveCaptionOn: boolean = false
  private activeCues: Map<string, Map<string, VTTCue>> = new Map();
  private audioSendBufferBytes: number = 0
  private audioSendBuffer: ArrayBuffer[] = []
  constructor(private t: TFunction) {
    super()
    this.user = useUserStore()
    this.app = useAppStore()
    this.session = useSessionStore()
    this.notification = useNotificationStore()
    this.transform = useTransformStore()
  }

  install = () => {
    jsonRpc.on('con:ready', this.verifyAllowLiveCaption)
    jsonRpc.on('stt:start', this.handleStartLiveCaption)
    jsonRpc.on('stt:stop', this.handleStopLiveCaption)
    jsonRpc.on('stt:transcript', this.handleTranscript)
    localStream.audio.on("replaceTrack", this.handleReplaceAudio)
  }

  uninstall = () => {
    jsonRpc.off('con:ready', this.verifyAllowLiveCaption)
    jsonRpc.off('stt:start', this.handleStartLiveCaption)
    jsonRpc.off('stt:stop', this.handleStopLiveCaption)
    jsonRpc.off('stt:transcript', this.handleTranscript)
    localStream.audio.off("replaceTrack", this.handleReplaceAudio)
    // Without the stt:stop listener nothing could stop the worklet any more
    this.isLiveCaptionOn = false
    this.closeAudioProcessor()
  }

  verifyAllowLiveCaption = (event: CustomEvent<{ party: string }>) => {
    if (event.detail.party === 'client' || this.user.party === 'client') {
      const { send } = useAppMachine()
      send('ALLOW_LIVE_CAPTION')
    }
  }

  start = async () => {
    await jsonRpc.sendAndWait(startLiveCaption())
  }
  stop = async () => {
    await jsonRpc.sendAndWait(stopLiveCaption())
  }

  closeAudioProcessor = async () => {
    this.audioProcessorVersion++
    if (this.audioProcessor) {
      const { context, worklet, source } = this.audioProcessor
      this.audioProcessor = undefined
      // Drop chunks the worklet already posted, so nothing is queued for sending after close
      worklet.port.onmessage = null;
      worklet.disconnect();
      source.disconnect();
      if (context.state !== "closed") {
        await context.close();
      }
    }
  }

  createAudioProcessor = async (audioTrack: MediaStreamTrack) => {
    // closeAudioProcessor bumps the version before its first await, so the version read here
    // belongs to this call; any later create or close makes this call give up (latest call wins)
    const closing = this.closeAudioProcessor();
    const version = this.audioProcessorVersion
    await closing;
    const context = new AudioContext({ sampleRate: 48000 });
    await context.audioWorklet.addModule("/pcm-worklet.js");
    if (version !== this.audioProcessorVersion) {
      // Stopped or superseded while the worklet module was loading
      await context.close();
      return
    }
    const stream = new MediaStream([audioTrack]);
    const source = context.createMediaStreamSource(stream);
    const worklet = new AudioWorkletNode(
      context,
      "pcm-worklet"
    );
    source.connect(worklet);
    worklet.port.onmessage = ({ data }) => {
      this.enqueueAudioChunk(data);
    };
    this.audioProcessor = { source, worklet, context }
  }

  handleStartLiveCaption = async () => {
    // start sending binary
    const notification = useNotificationStore()
    notification.showMessage({
      text: this.t('notification.liveCaptionStarted'),
      icon: 'live-caption',
      auto: true
    })
    this.app.transition('START_LIVE_CAPTION')
    this.isLiveCaptionOn = true
    await this.createAudioProcessor(localStream.audio.getTrack())
  }

  enqueueAudioChunk = (buffer: ArrayBuffer) => {
    this.audioSendBuffer.push(buffer)
    this.audioSendBufferBytes += buffer.byteLength
    if (this.audioSendBufferBytes >= AUDIO_SEND_CHUNK_BYTES) {
      this.flushAudioSendBuffer()
    }
  }

  flushAudioSendBuffer = () => {
    if (this.audioSendBufferBytes === 0) {
      return
    }
    const merged = new Uint8Array(this.audioSendBufferBytes)
    let offset = 0
    for (const buffer of this.audioSendBuffer) {
      merged.set(new Uint8Array(buffer), offset)
      offset += buffer.byteLength
    }
    this.audioSendBuffer = []
    this.audioSendBufferBytes = 0
    jsonRpc.sendBinary(merged.buffer)
  }

  handleStopLiveCaption = async () => {
    this.isLiveCaptionOn = false
    const notification = useNotificationStore()
    notification.showMessage({
      text: this.t('notification.liveCaptionStopped'),
      icon: 'live-caption',
      auto: true
    })
    // Before the await, so an stt:start arriving meanwhile is not overwritten by a late STOP
    this.app.transition('STOP_LIVE_CAPTION')
    this.flushAudioSendBuffer();
    await this.closeAudioProcessor();
  }

  getVideoElement = () => {
    for (const e of document.querySelectorAll("video")) {
      if (e.checkVisibility()) {
        return e
      }
    }
    return null
  }

  getTextTrack = (videoElement: HTMLVideoElement, label: string) => {
    for (const track of videoElement.textTracks) {
      if (label === track.label) {
        return track
      }
    }
    return null
  }

  handleTranscript = async (event: CustomEvent<TrancsriptEvent>) => {
    const { party, transcript, isPartial, resultId, partyId, languageCode } = event.detail
    const video = this.getVideoElement();
    if (!video) {
      return
    }
    let textTrack = this.getTextTrack(video, partyId)
    if (!textTrack) {
      textTrack = video.addTextTrack("subtitles", partyId, languageCode);
      textTrack.oncuechange = () => this.onCueChanged(textTrack!)
    }
    let cues = this.activeCues.get(partyId)
    if (!cues) {
      cues = new Map();
      this.activeCues.set(partyId, cues)
    }

    let text = `${party}: ${transcript}`;

    // partial update
    if (isPartial) {
      text = text + "..."
      let cue = cues.get(resultId);

      if (!cue) {
        cue = new VTTCue(
          video.currentTime,
          video.currentTime + 5,
          text
        );
        textTrack.addCue(cue);
        cues.set(resultId, cue);
      } else {
        cue.text = text;
        cue.endTime = video.currentTime + 5;
      }
      return;
    }
    // final transcript
    let cue = cues.get(resultId);

    if (cue) {
      cue.text = text;
      cue.endTime = video.currentTime + 3;
      cues.delete(resultId);
    } else {
      cue = new VTTCue(
        video.currentTime,
        video.currentTime + 3,
        text
      );
      textTrack.addCue(cue);
    }
  }
  handleReplaceAudio = (event: CustomEvent<{ newTrack: MediaStreamTrack }>) => {
    // A microphone switch must not start streaming when live caption is off
    if (!this.isLiveCaptionOn) {
      return
    }
    const { newTrack } = event.detail
    this.createAudioProcessor(newTrack);
  }

  onCueChanged = (textTrack: TextTrack) => {
    this.emit('cueChanged', textTrack)
  }
}
